import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WifiIcon from "@material-ui/icons/Wifi";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { StatusChip, tNegocio } from "./shared";
import { useStyles } from "./styles";
import { CodeLookupDialog } from "./CodeLookupDialog";
import { productOfferLabel } from "./catalog";

const SERVER_TYPES = {
  gmail: { host: "imap.gmail.com", port: 993, secure: true },
  hotmail: { host: "imap-mail.outlook.com", port: 993, secure: true },
  outlook: { host: "outlook.office365.com", port: 993, secure: true },
  domain: { host: "", port: 993, secure: true },
};

const serverTypeOf = (box) => {
  const host = String(box?.host || "").toLowerCase();
  if (host === "imap.gmail.com") return "gmail";
  if (host === "imap-mail.outlook.com") return "hotmail";
  if (host === "outlook.office365.com") return "outlook";
  return box?.host ? "domain" : "gmail";
};

const emptyBox = () => ({
  id: null,
  name: "",
  serverType: "gmail",
  host: "imap.gmail.com",
  port: 993,
  secure: true,
  user: "",
  password: "",
  enabled: true,
});

export default function CodeLookupSettings() {
  const classes = useStyles();
  const [boxes, setBoxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [lookup, setLookup] = useState({ productId: "", email: "" });
  const [lookupOpen, setLookupOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: imap }, { data: prod }] = await Promise.all([
        api.get("/negocio/imap"),
        api.get("/negocio/products"),
      ]);
      setBoxes(imap.records || []);
      setProducts(prod.records || []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveBox = async (box) => {
    try {
      const payload = { ...box, port: Number(box.port || 993) };
      if (box.id) await api.put(`/negocio/imap/${box.id}`, payload);
      else await api.post("/negocio/imap", payload);
      toast.success(tNegocio("toastSaved"));
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const removeBox = async (box) => {
    try {
      await api.delete(`/negocio/imap/${box.id}`);
      toast.success(tNegocio("toastSaved"));
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const offerLabel = (product) => productOfferLabel(product, tNegocio, { stock: false });
  const labelCount = products.reduce((acc, product) => {
    const label = offerLabel(product);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const lookupProducts = [...products].sort((a, b) => offerLabel(a).localeCompare(offerLabel(b), "es"));
  const imapBoxes = boxes.filter((b) => b.kind !== "yopmail");
  const activeBoxes = imapBoxes.filter((b) => b.enabled !== false);
  const inactiveBoxes = imapBoxes.filter((b) => b.enabled === false);

  return (
    <div>
      <div className={classes.captionBlock} style={{ marginBottom: 16 }}>
        {tNegocio("pages.codes.imapHint")}
      </div>
      <div style={{ marginBottom: 12 }}>
        <Button color="primary" variant="outlined" disabled={imapBoxes.length >= 4} onClick={() => saveBox(emptyBox())}>
          {tNegocio("pages.codes.addServer")}
        </Button>
      </div>
      <div className={classes.imapGrid}>
        {activeBoxes.map((box, index) => (
          <ImapCard
            key={box.id || `new-${index}`}
            box={box}
            index={index}
            canAdd={imapBoxes.length < 4}
            onSave={saveBox}
            onDelete={removeBox}
            onReload={load}
          />
        ))}
      </div>
      {inactiveBoxes.length ? (
        <div style={{ marginTop: 16 }}>
          <div className={classes.sectionTitle}>{tNegocio("pages.codes.inactiveServers")}</div>
          {inactiveBoxes.map((box) => (
            <div key={box.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ flex: 1, fontSize: 13 }}>{box.user || box.name || tNegocio("pages.codes.server")}</span>
              <Switch color="primary" size="small" checked={false} onChange={() => saveBox({ ...box, enabled: true, password: "" })} />
            </div>
          ))}
        </div>
      ) : null}
      <div className={classes.imapOperatorCard}>
        <div className={classes.sectionTitle} style={{ marginTop: 0 }}>
          {tNegocio("pages.codes.operatorTitle")}
        </div>
        <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
          {tNegocio("pages.codes.operatorHint")}
        </div>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("cols.product")}
          value={lookup.productId}
          onChange={(e) => setLookup((s) => ({ ...s, productId: e.target.value }))}
        >
          {lookupProducts.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {offerLabel(p)}
              {labelCount[offerLabel(p)] > 1 ? ` · #${p.id}` : ""}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.email")}
          value={lookup.email}
          onChange={(e) => setLookup((s) => ({ ...s, email: e.target.value }))}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!lookup.productId || !lookup.email}
          onClick={() => setLookupOpen(true)}
          style={{ marginTop: 12 }}
        >
          {tNegocio("pages.codes.lookup")}
        </Button>
      </div>
      <CodeLookupDialog
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        productId={lookup.productId}
        email={lookup.email}
      />
    </div>
  );
}

function ServerGuide({ type }) {
  const classes = useStyles();
  if (type === "gmail") {
    return (
      <div className={classes.imapHint}>
        <InfoOutlinedIcon />
        <span>
          {tNegocio("pages.codes.gmailGuide")}{" "}
          <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noopener noreferrer">
            {tNegocio("pages.codes.gmailTwoStepLink")}
          </a>
          {" · "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
            {tNegocio("pages.codes.gmailAppLink")}
          </a>
        </span>
      </div>
    );
  }
  if (type === "hotmail") {
    return (
      <div className={classes.imapHint}>
        <InfoOutlinedIcon />
        <span>
          {tNegocio("pages.codes.hotmailGuide")}{" "}
          <a href="https://account.live.com/proofs/AppPassword" target="_blank" rel="noopener noreferrer">
            {tNegocio("pages.codes.appPasswordLink")}
          </a>
        </span>
      </div>
    );
  }
  if (type === "outlook") {
    return (
      <div className={classes.imapHint}>
        <InfoOutlinedIcon />
        <span>{tNegocio("pages.codes.outlookGuide")}</span>
      </div>
    );
  }
  return (
    <div className={classes.imapHint}>
      <InfoOutlinedIcon />
      <span>{tNegocio("pages.codes.domainGuide")}</span>
    </div>
  );
}

function ImapCard({ box, index, canAdd, onSave, onDelete, onReload }) {
  const classes = useStyles();
  const [form, setForm] = useState({ ...box, serverType: serverTypeOf(box) });
  const [showPass, setShowPass] = useState(false);
  const [testing, setTesting] = useState(false);
  const [details, setDetails] = useState([]);
  useEffect(() => {
    setForm({ ...box, serverType: serverTypeOf(box) });
    setDetails([]);
  }, [box]);
  const patch = (next) => setForm((f) => ({ ...f, ...next }));
  const status = form.enabled === false ? "disabled" : "active";
  const cardTone = form.lastError ? classes.imapCardErr : form.lastOkAt ? classes.imapCardOk : "";

  const testBox = async () => {
    if (!form.user) {
      toast.error(tNegocio("pages.codes.userRequired"));
      return;
    }
    if (!form.password && !form.passwordSet) {
      toast.error(tNegocio("pages.codes.passwordRequired"));
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post(form.id ? `/negocio/imap/${form.id}/test` : "/negocio/imap/test", {
        id: form.id,
        host: form.host,
        port: Number(form.port || 993),
        user: form.user,
        password: form.password || "",
        secure: form.secure !== false,
      });
      const count = Number(data?.server_info?.messages || 0);
      setDetails(data?.details || []);
      toast.success(
        count
          ? `${tNegocio("pages.codes.imapOk")} · ${tNegocio("pages.codes.messagesCount").replace("{n}", String(count))}`
          : tNegocio("pages.codes.imapOk")
      );
      if (form.id) await onReload();
    } catch (error) {
      const extra = error?.response?.data?.details;
      if (Array.isArray(extra)) setDetails(extra);
      toastError(error);
      if (form.id) await onReload();
    } finally {
      setTesting(false);
    }
  };

  const chooseType = (serverType) => {
    const preset = SERVER_TYPES[serverType] || SERVER_TYPES.gmail;
    patch({
      serverType,
      host: serverType === "domain" ? (serverTypeOf(form) === "domain" ? form.host : "") : preset.host,
      port: preset.port,
      secure: preset.secure,
    });
  };

  const toggleEnabled = (checked) => {
    const next = { ...form, enabled: checked };
    patch({ enabled: checked });
    onSave({ ...next, password: form.password || "" });
  };

  return (
    <div className={`${classes.imapCard} ${cardTone}`.trim()}>
      <div className={classes.imapCardHead}>
        <div className={classes.imapCardIdentity}>
          <span className={classes.imapCardIcon}>
            <MailOutlineIcon />
          </span>
          <div>
            <div className={classes.imapCardTitle}>
              {tNegocio("pages.codes.server")} {index + 1}
            </div>
            <div className={classes.imapCardEmail}>
              {form.user || tNegocio("pages.codes.userPlaceholder")}
            </div>
          </div>
        </div>
        <div className={classes.imapCardToggle}>
          <StatusChip value={status} />
          <Switch
            color="primary"
            size="small"
            checked={form.enabled !== false}
            onChange={(e) => toggleEnabled(e.target.checked)}
          />
        </div>
      </div>
      <div className={classes.imapCardBody}>
        {form.lastOkAt && !form.lastError ? (
          <div className={classes.imapHint} style={{ color: "#15803d", marginTop: 0 }}>
            <CheckCircleIcon style={{ color: "#16a34a" }} />
            {tNegocio("pages.codes.lastOk")}
          </div>
        ) : null}
        {form.lastError ? (
          <div className={classes.imapHint} style={{ color: "#c62828", marginTop: 0 }}>
            <InfoOutlinedIcon style={{ color: "#c62828" }} />
            {form.lastError}
          </div>
        ) : null}
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.serverType")}
          value={form.serverType || "gmail"}
          onChange={(e) => chooseType(e.target.value)}
        >
          <MenuItem value="gmail">Gmail</MenuItem>
          <MenuItem value="hotmail">Hotmail</MenuItem>
          <MenuItem value="outlook">Outlook</MenuItem>
          <MenuItem value="domain">{tNegocio("pages.codes.serverDomain")}</MenuItem>
        </TextField>
        {form.serverType === "domain" ? (
          <div className={classes.imapHostRow}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.codes.host")}
              value={form.host || ""}
              onChange={(e) => patch({ host: e.target.value })}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              label={tNegocio("pages.codes.portHint")}
              value={form.port || 993}
              onChange={(e) => patch({ port: e.target.value })}
            />
          </div>
        ) : null}
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.user")}
          value={form.user || ""}
          onChange={(e) => patch({ user: e.target.value, name: form.name || e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type={showPass ? "text" : "password"}
          label={tNegocio("pages.codes.passwordApp")}
          placeholder={form.passwordSet ? "••••••••••" : ""}
          value={form.password || ""}
          onChange={(e) => patch({ password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.passwordSet && !form.password ? (
                  <Chip
                    size="small"
                    label={tNegocio("pages.codes.passwordConfigured")}
                    className={classes.imapChipOk}
                    style={{ marginRight: 4 }}
                  />
                ) : null}
                <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                  {showPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <ServerGuide type={form.serverType || "gmail"} />
        {form.serverType === "domain" ? (
          <FormControlLabel
            control={<Switch color="primary" checked={form.secure !== false} onChange={(e) => patch({ secure: e.target.checked })} />}
            label={tNegocio("pages.codes.secure")}
          />
        ) : null}
        {details.length ? <div className={classes.imapDetails}>{details.join("\n")}</div> : null}
        <div className={classes.imapFooter}>
          <Button
            color="primary"
            variant="contained"
            className={classes.imapTestBtn}
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <WifiIcon />}
            onClick={testBox}
            disabled={testing || (!canAdd && !form.id && !form.user)}
          >
            {testing ? tNegocio("pages.codes.testing") : tNegocio("pages.codes.test")}
          </Button>
          <span className={classes.captionInline}>{tNegocio("pages.codes.testHint")}</span>
          <span style={{ flex: 1 }} />
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              if (form.serverType === "domain" && !String(form.host || "").trim()) {
                toast.error(tNegocio("pages.codes.domainHostRequired"));
                return;
              }
              onSave(form);
            }}
            disabled={!canAdd && !form.id}
          >
            {tNegocio("save")}
          </Button>
          {form.id ? (
            <Tooltip title={tNegocio("pages.codes.removeMailbox")}>
              <IconButton onClick={() => onDelete(form)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}
